import React, {FC, useMemo, useState} from 'react';
import {useInformerConfigState} from '@/informers/services/informerConfigReducer';
import {useLocalizer} from '@/common/services/localizer';
import {throttle} from '@/common/helpers/utils/throttle';
import {Radio} from '@/common/components/Radio';
import {FieldText} from '@/common/components/FieldText';
import {FieldSelect} from '@/common/components/FieldSelect';
import {FieldRange} from '@/common/components/FieldRange';
import styles from './FormSize.css';

type SizeTypeOption = 'adaptive' | 'fixed';
type LayoutOption = 'vertical' | 'horizontal';

const sizeTypeOptions: SizeTypeOption[] = ['adaptive', 'fixed'];
const layoutOptions: LayoutOption[] = ['vertical', 'horizontal'];

const manualSizeRange = {
	min: 120,
	max: 350,
};

interface FormSizeProps {
	onNextStage(): void;
}

export const FormSize: FC<FormSizeProps> = props => {
	const {onNextStage} = props;

	const t = useLocalizer();

	const {state, dispatch} = useInformerConfigState();

	const [localWidth, setLocalWidth] = useState<number | null>(state.width);

	const manualSizeFactor = state.layout === 'horizontal' ? state.locations.length || 1 : 1;

	const handleComplete = (event: React.FormEvent) => {
		event.preventDefault();
		onNextStage();
	};

	const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.currentTarget.value as SizeTypeOption;
		dispatch({type: 'setValues', values: {sizeType: value}});
	};

	const handleLayoutChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const value = event.currentTarget.value as LayoutOption;
		dispatch({type: 'setValues', values: {layout: value}});
	};

	const applyNewWidth = useMemo(
		() => throttle((width: number) => dispatch({type: 'setValues', values: {width}}), 100),
		[],
	);

	const handleCustomWidthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const valueString = event.currentTarget.value;
		if (!valueString) {
			setLocalWidth(null);
			return;
		}

		let value = Number(valueString);
		value = Math.round(value / manualSizeFactor);
		value = Math.min(value, manualSizeRange.max);
		setLocalWidth(value);
		applyNewWidth(value);
	};

	const handleCustomWidthRepair = () => {
		let newWidth;
		if (!localWidth || localWidth < manualSizeRange.min) {
			newWidth = manualSizeRange.min;
		} else if (localWidth > manualSizeRange.max) {
			newWidth = manualSizeRange.max;
		}

		if (newWidth) {
			dispatch({type: 'setValues', values: {width: newWidth}});
			setLocalWidth(newWidth);
		}
	};

	const getLayoutTypeName = (option: string) => t(`layoutType.${option}`);

	return (
		<form className={styles.root} onSubmit={handleComplete}>
			<p className={styles.labelText}>{t('layoutTitle')}</p>
			<FieldSelect
				className={styles.layoutSelect}
				options={layoutOptions}
				value={state.layout}
				disabled={state.locations.length < 2}
				getDisplayValue={getLayoutTypeName}
				onChange={handleLayoutChange}
			/>

			{sizeTypeOptions.map(option => (
				<label key={option} className={styles.radioRow}>
					<Radio
						className={styles.radioInput}
						name='sizeType'
						checked={state.sizeType === option}
						value={option}
						onChange={handleTypeChange}
					/>
					<span className={styles.radioLabel}>{t(`sizeType.${option}`)}</span>
				</label>
			))}

			{state.sizeType === 'fixed' && (
				<>
					<FieldRange
						className={styles.rangeField}
						value={(localWidth === null ? manualSizeRange.min : localWidth) * manualSizeFactor}
						min={manualSizeRange.min * manualSizeFactor}
						max={manualSizeRange.max * manualSizeFactor}
						onChange={handleCustomWidthChange}
					/>
					<div className={styles.sizeFieldWrapper}>
						<FieldText
							className={styles.sizeField}
							type='number'
							min={manualSizeRange.min * manualSizeFactor}
							max={manualSizeRange.max * manualSizeFactor}
							value={localWidth === null ? '' : localWidth * manualSizeFactor}
							onChange={handleCustomWidthChange}
							onBlur={handleCustomWidthRepair}
						/>
						px
					</div>
				</>
			)}
		</form>
	);
};

FormSize.displayName = 'FormSize';
